import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ProductRootState,
  ProductVariant,
  SelectedTemplateType,
  AddLineItems,
} from './product-model';

export const PRODUCT_FEATURE_KEY = 'productData';

export interface ProductState {
  selectedProducts: ProductVariant[];
  selectedProductsIds: number[];
  selectedTemplate: SelectedTemplateType;
  productLineItems: AddLineItems[];
}

export const initialProductState: ProductState = {
  selectedProducts: [],
  selectedProductsIds: [],
  selectedTemplate: SelectedTemplateType.PARENT_PRODUCT_CATALOGUE,
  productLineItems: [],
};

export const productSlice = createSlice({
  name: PRODUCT_FEATURE_KEY,
  initialState: initialProductState,
  reducers: {
    updateSelectedProductIds: (
      state,
      action: PayloadAction<ProductVariant['id']>
    ) => {
      state.selectedProductsIds.push(action.payload);
    },
    removeSelectedProductIds: (
      state,
      action: PayloadAction<ProductVariant['id']>
    ) => {
      state.selectedProductsIds = state.selectedProductsIds.filter(
        (productId) => productId !== action.payload
      );
    },
    updateSelectedProducts: (
      state,
      action: PayloadAction<ProductState['selectedProducts']>
    ) => {
      state.selectedProducts = action.payload;
    },
    removeSelectedProducts: (
      state,
      action: PayloadAction<ProductVariant['id']>
    ) => {
      state.selectedProducts = state.selectedProducts.filter(
        (product) => product.id !== action.payload
      );
    },
    updateSelectedTemplate: (
      state,
      action: PayloadAction<ProductState['selectedTemplate']>
    ) => {
      state.selectedTemplate = action.payload;
    },
    updateProductLineItems: (
      state,
      action: PayloadAction<ProductState['productLineItems']>
    ) => {
      const newItems = action.payload;

      if (newItems.length === 0) {
        state.productLineItems = [];
        return;
      }

      if (!Array.isArray(state.productLineItems)) {
        state.productLineItems = [];
      }

      const updatedItems = [...state.productLineItems];

      newItems.forEach((newItem) => {
        const { productVariantId, quantity, replaceProductVariantId } = newItem;

        // Handle replacement if applicable
        if (
          replaceProductVariantId &&
          replaceProductVariantId !== productVariantId
        ) {
          const replaceIndex = updatedItems.findIndex(
            (item) => item.productVariantId === replaceProductVariantId
          );
          if (replaceIndex !== -1) {
            updatedItems.splice(replaceIndex, 1);
          }
        }

        // Normal map and add logic
        const existingIndex = updatedItems.findIndex(
          (item) => item.productVariantId === productVariantId
        );

        if (quantity > 0) {
          if (existingIndex !== -1) {
            updatedItems[existingIndex] = newItem;
          } else {
            updatedItems.push(newItem);
          }
        } else {
          if (existingIndex !== -1) {
            updatedItems.splice(existingIndex, 1);
          }
        }
      });

      state.productLineItems = updatedItems;
    },
  },
});

export const productReducer = productSlice.reducer;

export const {
  updateProductLineItems,
  updateSelectedProductIds,
  removeSelectedProductIds,
  updateSelectedProducts,
  removeSelectedProducts,
  updateSelectedTemplate,
} = productSlice.actions;

export const getproductsSliceState = (
  rootState: ProductRootState
): ProductState => rootState[PRODUCT_FEATURE_KEY];

export const getProductLineItems = createSelector(
  getproductsSliceState,
  (state) => state.productLineItems
);

export const getSelectedProducts = createSelector(
  getproductsSliceState,
  (state) => state.selectedProducts
);

export const getSelectedTemplate = createSelector(
  getproductsSliceState,
  (state) => state.selectedTemplate
);
